import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { paymentsApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  User, CreditCard, Shield, Bell, LogOut, CheckCircle, 
  AlertCircle, Crown, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, updateProfile, logout, isPremium } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [profile, setProfile] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    state: user?.state || '',
    zip_code: user?.zip_code || ''
  });

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfile(profile);
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const { url } = await paymentsApi.createCheckout('premium', window.location.origin);
      window.location.href = url;
    } catch (err) {
      toast.error('Failed to start upgrade');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Settings
        </h1>
        <p className="text-slate-600 mt-1">Manage your account and preferences.</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-4">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="profile" data-testid="profile-tab">
            <User className="h-4 w-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="subscription" data-testid="subscription-tab">
            <CreditCard className="h-4 w-4 mr-2" />
            Subscription
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="security-tab">
            <Shield className="h-4 w-4 mr-2" />
            Security
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="border-slate-100 shadow-sm" data-testid="profile-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Profile Information</CardTitle>
              <CardDescription>Update your personal information</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={profile.first_name}
                      onChange={(e) => setProfile(prev => ({ ...prev, first_name: e.target.value }))}
                      className="h-11"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={profile.last_name}
                      onChange={(e) => setProfile(prev => ({ ...prev, last_name: e.target.value }))}
                      className="h-11"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={user?.email || ''}
                    disabled
                    className="h-11 bg-slate-50"
                  />
                  <p className="text-xs text-slate-500">Email cannot be changed</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={profile.phone}
                    onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                    className="h-11"
                    data-testid="phone-input"
                  />
                </div>
                <Separator />
                <div className="space-y-2">
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    value={profile.address}
                    onChange={(e) => setProfile(prev => ({ ...prev, address: e.target.value }))}
                    className="h-11"
                    data-testid="address-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={profile.city}
                      onChange={(e) => setProfile(prev => ({ ...prev, city: e.target.value }))}
                      className="h-11"
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={profile.state}
                      onChange={(e) => setProfile(prev => ({ ...prev, state: e.target.value }))}
                      className="h-11"
                      data-testid="state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">ZIP Code</Label>
                    <Input
                      id="zip_code"
                      value={profile.zip_code}
                      onChange={(e) => setProfile(prev => ({ ...prev, zip_code: e.target.value }))}
                      className="h-11"
                      data-testid="zip-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="btn-teal"
                  disabled={loading}
                  data-testid="save-profile-btn"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription">
          <Card className="border-slate-100 shadow-sm" data-testid="subscription-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Subscription</CardTitle>
              <CardDescription>Manage your subscription plan</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Plan */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    isPremium ? 'bg-teal-100' : 'bg-slate-200'
                  }`}>
                    {isPremium ? (
                      <Crown className="h-5 w-5 text-teal-600" />
                    ) : (
                      <User className="h-5 w-5 text-slate-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">
                      {isPremium ? 'Premium Plan' : 'Free Plan'}
                    </p>
                    <p className="text-sm text-slate-500">
                      {isPremium ? '$29.99/month' : 'No charge'}
                    </p>
                  </div>
                </div>
                <Badge className={isPremium ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-700'}>
                  {user?.subscription_status || 'Active'}
                </Badge>
              </div>

              {/* Features */}
              <div>
                <h3 className="font-medium text-slate-900 mb-3">Plan Features</h3>
                <ul className="space-y-2">
                  {isPremium ? (
                    <>
                      <li className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-teal-600" />
                        Automatic dispute sending
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-teal-600" />
                        Monthly automation cycles
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-teal-600" />
                        AI Credit Coach (unlimited)
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-teal-600" />
                        Real-time monitoring & alerts
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-teal-600" />
                        Priority support
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-slate-400" />
                        Credit insights & education
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-slate-400" />
                        Manual letter download
                      </li>
                      <li className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle className="h-4 w-4 text-slate-400" />
                        Basic support
                      </li>
                    </>
                  )}
                </ul>
              </div>

              {/* Upgrade Button */}
              {!isPremium && (
                <div className="p-4 bg-teal-50 rounded-xl border border-teal-200">
                  <div className="flex items-start gap-3 mb-3">
                    <Crown className="h-5 w-5 text-teal-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-teal-800">Upgrade to Premium</p>
                      <p className="text-sm text-teal-700">
                        Get automatic dispute sending and unlock all features.
                      </p>
                    </div>
                  </div>
                  <Button 
                    className="btn-teal w-full"
                    onClick={handleUpgrade}
                    disabled={loading}
                    data-testid="upgrade-btn"
                  >
                    {loading ? 'Processing...' : 'Upgrade for $29.99/month'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card className="border-slate-100 shadow-sm" data-testid="security-card">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope' }}>Security</CardTitle>
              <CardDescription>Manage your account security</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Authorization Status */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    user?.is_authorized ? 'bg-green-100' : 'bg-amber-100'
                  }`}>
                    {user?.is_authorized ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-amber-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Authorization Status</p>
                    <p className="text-sm text-slate-500">
                      {user?.is_authorized 
                        ? `Authorized on ${new Date(user.authorization_date).toLocaleDateString()}`
                        : 'Not yet authorized'}
                    </p>
                  </div>
                </div>
                {!user?.is_authorized && (
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/onboarding')}
                    data-testid="complete-auth-btn"
                  >
                    Complete
                  </Button>
                )}
              </div>

              <Separator />

              {/* Logout */}
              <div>
                <h3 className="font-medium text-slate-900 mb-3">Sign Out</h3>
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setShowLogoutConfirm(true)}
                  data-testid="logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Logout Confirmation */}
      <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <AlertDialogContent data-testid="logout-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sign Out?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign out of your account?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-logout-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-logout-btn"
            >
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
